/**
 * Module 54 (WP-54.1) — Block Jam TOP BAR visual config: schema, typed loader,
 * in-bundle default.
 *
 * Mirrors `board-visual-config.ts` exactly: the single shipped, editable
 * artifact is `games/blockjam/config/topbar-visuals.json` (the Top Bar Studio
 * tab writes it, WP-54.5; the composition root reads it at boot, WP-54.3). That
 * file lives OUTSIDE the package `rootDir` (`src`), so the compiled library
 * cannot import it — the parity default is authored here as
 * `DEFAULT_TOPBAR_VISUAL_CONFIG` and a drift test
 * (`topbar-visual-config.test.ts`) asserts the JSON parses to exactly this
 * value.
 *
 * ## What this config owns, and what it does not
 *
 * Scope is the Top Bar **strip's chrome** — its background paint, its bottom
 * hairline and its shadow — and nothing else (`planning/54-topbar-studio/CLAUDE.md`
 * §3 decision 5). It owns no geometry: the bar's height, padding, safe-area and
 * `1fr auto 1fr` grid belong to the locked shared `.app-top-bar` frame in
 * `packages/ui`, and Row 2's band, the Level/Moves pills and the currency badge
 * are all outside it.
 *
 * That split is why `mode: 'transparent'` is paint-only: it resolves the
 * background, border and shadow to `none` and leaves every geometric property
 * untouched, so no Module 48 layout invariant (TOPBAR / GUTTER / MIDLINE /
 * SYMMETRY) can move when it is switched on.
 *
 * Unlike the board and block configs, nothing here is authored against a
 * reference cell size: these values reach CSS as literal pixels and alphas, not
 * as canvas paint that scales with the board.
 */
import { z } from 'zod';
// ─── Schema ───────────────────────────────────────────────────────────────────
const hexColour = z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, 'must be a 6-digit hex colour like #0d0028');
const opacity = z.number().min(0).max(1);
/**
 * The strip's paint. `gradient*` fields are only read when `mode === 'gradient'`
 * and `colour`/`opacity` only when `mode === 'solid'` — all are kept present
 * either way so switching modes in the Studio never loses the other mode's
 * values (the `surfaceSchema` convention from `board-visual-config.ts`).
 *
 * Each gradient stop carries its own opacity, which `board-visuals`' surface
 * does not. That is deliberate and it is what makes the shipped default an
 * exact reproduction of today's alpha ramp (0.97 → 0.92) rather than a flat
 * average of it; it is also the knob a translucent "floating" bar is actually
 * tuned with.
 */
const backgroundSchema = z.object({
    mode: z.enum(['solid', 'gradient']),
    colour: hexColour,
    opacity,
    gradientFrom: hexColour,
    gradientFromOpacity: opacity,
    gradientTo: hexColour,
    gradientToOpacity: opacity,
    /** Gradient direction in degrees, CSS `linear-gradient` convention: 0° up. */
    gradientAngle: z.number().min(0).max(360),
});
/**
 * The bottom hairline. Block Jam now owns this: it is declared on the SHARED
 * `.app-top-bar` frame (`ui.css`, WP-48.12) for all three surfaces, and
 * `.app-top-bar.bj-topbar` overrides it unconditionally — as `1px solid
 * rgba(...)` or as `none`, never by leaving a var unset (`CLAUDE.md` §4).
 */
const borderSchema = z.object({
    enabled: z.boolean(),
    colour: hexColour,
    opacity,
    /** Bottom border width in CSS px. */
    thickness: z.number().min(0).max(8),
    /**
     * Corner rounding in CSS px. `0` on the shipped default — the strip is
     * full-bleed today — and the knob a future inset/floating card would need.
     */
    radius: z.number().min(0).max(40),
});
/** The shadow the bar casts onto the playfield. Also shared-frame-owned today. */
const shadowSchema = z.object({
    enabled: z.boolean(),
    colour: hexColour,
    opacity,
    /** Blur radius in CSS px. */
    blur: z.number().min(0).max(60),
    offsetX: z.number().min(-20).max(20),
    offsetY: z.number().min(-20).max(20),
});
export const topBarVisualConfigSchema = z.object({
    schemaVersion: z.literal(1),
    /**
     * `strip` paints the bar as a solid region; `transparent` paints nothing at
     * all — controls sitting directly on the game background (TopBarRef_1) — with
     * the bar's geometry frozen either way (`CLAUDE.md` §3 decision 4).
     */
    mode: z.enum(['strip', 'transparent']),
    background: backgroundSchema,
    border: borderSchema,
    shadow: shadowSchema,
});
// ─── Loader ───────────────────────────────────────────────────────────────────
/**
 * Parse + validate an untyped config object (the JSON file the Studio wrote, or
 * a fixture). Throws a `ZodError` with a readable path on invalid input.
 */
export function loadTopBarVisualConfig(raw) {
    return topBarVisualConfigSchema.parse(raw);
}
// ─── Default (today's appearance, unchanged) ──────────────────────────────────
/**
 * Kept in sync with `games/blockjam/config/topbar-visuals.json` — the drift test
 * enforces `loadTopBarVisualConfig(json)` deep-equals this value.
 *
 * **`mode` is the one field the user tuned** (Module 54, C.4, 2026-08-15), live
 * in the Top Bar Studio tab: the bar ships `transparent`, so Block Jam's
 * controls sit directly on the game background — `Bugs/TopBarRef_1.jpeg`. It is
 * a paint change and only a paint change: the bar keeps its full width, its
 * `--app-top-bar-h` height and the board reserve derived from it, which
 * `smoke-platform.mjs`'s PAINTONLY check measures on every profile and in both
 * orientations.
 *
 * Every OTHER value below is untouched, and deliberately so — they are carried,
 * not dead. They are what `mode: 'strip'` still paints, they are what the Studio
 * restores the moment the mode is switched back, and they remain the frozen
 * record of the pre-Module-54 bar (`topbar-vars.test.ts` asserts they compose
 * exactly the old declarations). That record is the reason the seam could be
 * shown to have landed inert before anything was tuned:
 *
 * - `background` — `blockjam.css`'s `.bj-topbar` gradient
 *   `linear-gradient(180deg, rgba(13,0,40,.97) 0%, rgba(16,2,48,.94) 60%,
 *   rgba(20,4,56,.92) 100%)`, as the schema's two stops at the same angle. The
 *   endpoint colours and alphas are EXACT; the deliberate approximation is the
 *   dropped 60% stop, which the two-stop interpolation reproduces to within
 *   1.2/255 (red), 0.4/255 (green) and 1.6/255 (blue), at an identical 0.94
 *   alpha. That is the "reproduced closely at the same angle" of `CLAUDE.md` §3
 *   decision 6, and it is the module's ONLY intended pixel delta.
 * - `border` / `shadow` — `ui.css`'s shared `.app-top-bar`
 *   `border-bottom: 1px solid rgba(255,255,255,.1)` and
 *   `box-shadow: 0 2px 10px rgba(0,0,0,.32)` (WP-48.12). Block Jam overrides
 *   rather than inherits them from WP-54.3 on, so it has to carry their values.
 *
 * `background.colour`/`opacity` are the solid-mode carry values (the gradient's
 * midpoint), meaningful only if the Studio flips the background fill;
 * `border.radius` is 0 because the strip is full-bleed.
 */
export const DEFAULT_TOPBAR_VISUAL_CONFIG = loadTopBarVisualConfig({
    schemaVersion: 1,
    mode: 'transparent',
    background: {
        mode: 'gradient',
        colour: '#100230',
        opacity: 0.94,
        gradientFrom: '#0d0028',
        gradientFromOpacity: 0.97,
        gradientTo: '#140438',
        gradientToOpacity: 0.92,
        gradientAngle: 180,
    },
    border: { enabled: true, colour: '#ffffff', opacity: 0.1, thickness: 1, radius: 0 },
    shadow: { enabled: true, colour: '#000000', opacity: 0.32, blur: 10, offsetX: 0, offsetY: 2 },
});
